/**
 * Contacts — HX address book. Polished table with tag chips, hover
 * elevation, and one-click call / SMS / edit / delete actions.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCall } from "@/context/CallContext";
import EmptyState from "@/components/EmptyState";
import DataTable from "@/components/DataTable";
import { Plus, Phone, Trash, ChatText, X, PencilSimple, AddressBook } from "@phosphor-icons/react";
import { HxCard, HxButton, HxChip, HxInput, HxLabel, HX_COLORS as C } from "@/hx";

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const { startCall } = useCall();

  const load = async () => {
    const { data } = await api.get("/contacts");
    setContacts(data);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await api.delete(`/contacts/${id}`);
    load();
  };

  const iconBtn = {
    width: 30, height: 30, borderRadius: 10, display: "grid", placeItems: "center",
    border: "1px solid var(--hx-line)", background: "#fff", color: "var(--hx-ink-2)",
    transition: "background-color .18s, border-color .18s, color .18s",
  };

  return (
    <div className="hbf-hx" data-testid="contacts-page">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="hx-hero hx-up">
        <div>
          <div className="hx-hero__eyebrow"><AddressBook size={16} weight="duotone" color={C.blue} /> Address book</div>
          <h1 className="hx-hero__title">Your <em>contacts</em></h1>
          <p className="hx-hero__sub">{contacts.length} saved — click-to-dial, message, and track history per contact.</p>
        </div>
        <div className="hx-hero__cta">
          <HxButton variant="primary" onClick={() => setShowAdd(true)} data-testid="add-contact-btn">
            <Plus size={16} weight="bold" /> Add contact
          </HxButton>
        </div>
      </header>

      <div className="hx-up hx-d1">
        {contacts.length === 0 ? (
          <HxCard>
            <EmptyState
              icon="contacts"
              title="No contacts yet"
              description="Build your address book to click-to-dial, send SMS, and view per-contact call history."
              primaryAction={{ label: "Add your first contact", onClick: () => setShowAdd(true) }}
              secondaryAction={{ label: "Import from CSV", onClick: () => alert("CSV import is on the roadmap.") }}
              learnMore={{ label: "Learn about contacts", href: "/developers" }}
              testId="empty-state-contacts"
            />
          </HxCard>
        ) : (
          <DataTable
            testId="contacts-table"
            data={contacts}
            rowKey={(c) => c.id}
            rowTestId={(c) => `contact-row-${c.id}`}
            searchPlaceholder="Search contacts by name, phone, email…"
            searchKeys={["name", "phone", "email", "company", "tags"]}
            columns={[
              {
                key: "name", label: "Name", sortable: true,
                render: (c) => (
                  <div className="flex items-center gap-2.5">
                    <img src={c.avatar_url} alt="" style={{ width: 32, height: 32, borderRadius: 11, objectFit: "cover" }} />
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{c.name}</div>
                  </div>
                ),
              },
              { key: "phone",   label: "Phone",   sortable: true, width: "170px", render: (c) => <span style={{ fontSize: 12.5, fontFamily: "var(--hx-mono)" }}>{c.phone}</span> },
              { key: "email",   label: "Email",   sortable: true, render: (c) => <span style={{ fontSize: 13, color: "var(--hx-ink-2)" }}>{c.email || "—"}</span> },
              { key: "company", label: "Company", sortable: true, width: "180px", render: (c) => <span style={{ fontSize: 13 }}>{c.company || "—"}</span> },
              {
                key: "tags", label: "Tags", width: "180px",
                render: (c) => (
                  <div className="flex gap-1 flex-wrap">
                    {(c.tags || []).map((t) => <HxChip key={t} tone="violet">{t}</HxChip>)}
                  </div>
                ),
              },
              {
                key: "_act", label: "", width: "190px",
                render: (c) => (
                  <div className="flex gap-1.5 items-center">
                    <button onClick={() => startCall({ to_number: c.phone, to_name: c.name })} className="hx-dial__call" data-testid={`contact-call-${c.id}`}>
                      <Phone size={12} weight="fill" /> Call
                    </button>
                    <button style={iconBtn} className="hover:!bg-[var(--hx-blue-soft)] hover:!text-[var(--hx-blue)]" data-testid={`contact-sms-${c.id}`} title="Send SMS"><ChatText size={14} /></button>
                    <button onClick={() => setEditing(c)} style={iconBtn} className="hover:!bg-[var(--hx-line-soft)]" data-testid={`contact-edit-${c.id}`} title="Edit"><PencilSimple size={14} /></button>
                    <button onClick={() => del(c.id)} style={{ ...iconBtn, color: "var(--hx-red)" }} className="hover:!bg-[var(--hx-red-soft)] hover:!border-[#F5C2C2]" data-testid={`contact-delete-${c.id}`} title="Delete"><Trash size={14} /></button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>

      {showAdd && <ContactModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <ContactModal contact={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ContactModal({ contact, onClose, onSaved }) {
  const isEdit = !!contact;
  const [form, setForm] = useState({
    name: contact?.name || "",
    phone: contact?.phone || "",
    email: contact?.email || "",
    company: contact?.company || "",
    tags: (contact?.tags || []).join(", "),
    notes: contact?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
    try {
      if (isEdit) {
        await api.put(`/contacts/${contact.id}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post("/contacts", payload);
        toast.success("Contact saved");
      }
      onSaved();
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    }
    setSaving(false);
  };

  return (
    <div className="hx-scrim" data-testid={isEdit ? "edit-contact-modal" : "add-contact-modal"}>
      <div className="hx-modal">
        <div className="hx-modal__head">
          <div className="hx-modal__title">{isEdit ? "Edit contact" : "Add contact"}</div>
          <button onClick={onClose} className="hx-btn hx-btn--ghost hx-btn--sm" data-testid="contact-modal-close"><X size={15} /></button>
        </div>
        <form onSubmit={submit} className="hx-modal__body space-y-4">
          <div><HxLabel>Name</HxLabel><HxInput required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="contact-name-input" /></div>
          <div><HxLabel>Phone</HxLabel><HxInput required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="contact-phone-input" placeholder="+1 (307) 240-2137" /></div>
          <div><HxLabel>Email</HxLabel><HxInput type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="contact-email-input" /></div>
          <div><HxLabel>Company</HxLabel><HxInput value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} data-testid="contact-company-input" /></div>
          <div><HxLabel>Tags (comma separated)</HxLabel><HxInput value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} data-testid="contact-tags-input" placeholder="VIP, Lead" /></div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="hx-btn hx-btn--ghost flex-1" style={{ justifyContent: "center" }}>Cancel</button>
            <button type="submit" disabled={saving} className="hx-btn hx-btn--primary flex-1" style={{ justifyContent: "center" }} data-testid="contact-save-btn">{saving ? "Saving..." : (isEdit ? "Save changes" : "Save contact")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
